<template>
  <v-container fluid>
    <v-card>
      <v-toolbar color="primary" density="comfortable">
        <v-toolbar-title class="text-white"> Pièces détachées </v-toolbar-title>
        <v-spacer />
        <div class="d-flex ga-2">
          <table-header-buttons
            :attr="toolTipText"
            @add="add"
            @remove="remove"
            @reload="reloadData"
            @export="exportTable"
            @search="updateSearchPanel"
            :hasSelectedLines="selectedSparepartIds.length > 0"
          />
        </div>
      </v-toolbar>
      <Table
        :loading="loading"
        :searchField="true"
        :columns="columns"
        :items="spareparts"
        :showSelect="true"
        @edit="edit"
        @rowClicked="rowClicked"
        @selectionChanged="selectionChanged"
      />
    </v-card>

    <!-- Overlay -->
    <v-overlay :model-value="loading" class="align-center justify-center">
      <v-progress-circular indeterminate size="64" />
    </v-overlay>

    <!-- Search panel -->
    <search-panel
      :rightDrawer="rightDrawer"
      @cancelSearch="cancelSearch"
      @searchData="searchSpareparts"
    >
      <v-row>
        <v-col cols="11" offset="1">
          <v-text-field label="Nom" v-model="searchFilter.contain.name" />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="11" offset="1">
          <v-text-field label="Reference" v-model="searchFilter.contain.reference" />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="11" offset="1">
          <v-text-field label="Telephone" v-model="searchFilter.contain.telephone" />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="11" offset="1">
          <v-text-field label="Courriel" v-model="searchFilter.contain.email" />
        </v-col>
      </v-row>
    </search-panel>

    <ConfirmDialog
      v-model="dialog"
      :dialogTitle="dialogTitle"
      :dialogText="dialogText"
      @onfirm="onConfirm"
      @cancel="onCancel"
    />
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";

import Table from "@/components/Table.vue";
import TableHeaderButtons from "@/components/TableHeaderButtons.vue";
import SearchPanel from "@/components/SearchPanel.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";

import {
  buildSearchFilters,
  buildJsonServerQuery,
  clearSearchFilters,
} from "@/utils/app-util";

import { useAppStore } from "@/store/app";
import { createAndSaveDocument } from "@/utils/docx-util";
import { getSparepartExport, Sparepart, TableColumn } from "@/types";

import {
  deleteSparepart,
  getAllSpareparts,
  getSparepartById,
  searchForSpareparts,
} from "@/utils/sparepart-api";

const router = useRouter();
const appStore = useAppStore();

const loading = ref(false);
const dialog = ref(false);
const dialogTitle = ref("Suppression");
const dialogText = ref("Voulez-vous supprimer les pièces sélectionnées?");
const showSearchPanel = ref(false);

const spareparts = ref<Sparepart[]>([]);
const selectedSparepartIds = ref<string[]>([]);

const columns: TableColumn[] = [
  { title: "Machine", key: "sparepartType", align: "start" },
  { title: "Status", key: "sparepartStatus" },
  { title: "Fabricant", key: "sparepartMake" },
  { title: "Désignation JLA", key: "name" },
  { title: "Référence", key: "reference" },
  { title: "Désignation française", key: "frenchName" },
  { title: "Prix (€)", key: "price" },
  { title: "Solde", key: "unitInStock" },
  { title: "Solde minimum", key: "minUnitInStock" },
  { title: "Commande", key: "command" },
  { title: "Total stock (€)", key: "stockValue" },
  { title: "", key: "actions", sortable: false },
];

const searchFilter = ref({
  contain: {
    name: "",
    reference: "",
    telephone: "",
    email: "",
  },
});

const query = ref("");

const toolTipText = computed(() => "une pièce détachée");

const rightDrawer = computed({
  get: () => showSearchPanel.value,
  set: (value: boolean) => (showSearchPanel.value = value),
});

function selectionChanged(selected: string[]) {
  selectedSparepartIds.value = [...selected];
}

function rowClicked(item: Sparepart) {
  router.push(`spareparts/${item.id}`);
}

function edit(item: Sparepart) {
  router.push(`spareparts/${item.id}`);
}

function add() {
  router.push("newsparepart");
}

function remove() {
  dialog.value = true;
}

async function onConfirm() {
  try {
    loading.value = true;
    dialog.value = false;

    await deleteSparepart(selectedSparepartIds.value);
    await fetchSpareparts();
    selectedSparepartIds.value = [];
    appStore.sendSuccessNotice("Sparepart has been deleted.");
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

function onCancel() {
  dialog.value = false;
}

function searchSpareparts() {
  showSearchPanel.value = false;
  buildSearchFilters(searchFilter.value);
  query.value = buildJsonServerQuery(searchFilter.value);
  _searchSpareparts(query.value);
}

async function _searchSpareparts(searchQuery: string) {
  try {
    loading.value = true;
    spareparts.value = await searchForSpareparts("spareparts?" + searchQuery);
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

function reloadData() {
  query.value = "";
  fetchSpareparts();
}

const exportTable = async () => {
  if (loading.value) {
    return;
  }

  const sparepartIds = [...selectedSparepartIds.value];
  if (!sparepartIds.length) {
    return;
  }

  try {
    loading.value = true;

    const selectedSpareparts = await Promise.all(
      sparepartIds.map((id) => getSparepartById(id))
    );

    createAndSaveDocument(
      "export_piecesDetachees",
      "../templates/ExportPiecesDetacheesTemplate.docx",
      getSparepartExport(selectedSpareparts)
    );
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
};

function updateSearchPanel() {
  rightDrawer.value = !rightDrawer.value;
}

function cancelSearch() {
  showSearchPanel.value = false;
}

async function fetchSpareparts() {
  try {
    loading.value = true;
    spareparts.value = await getAllSpareparts();
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

onMounted(fetchSpareparts);
</script>
<style scoped>
.title {
  font-size: 1.5rem;
  font-weight: bold;
}
</style>
